import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import { failAlert } from "../../../common/alert";
import { updateProductFilters } from "../../../redux/action/productFiltersAction";
import { useQueryClient } from "@tanstack/react-query";

const SizeFilterWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  > span {
    margin-right: 10px;
  }
  .btn {
    height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    padding: 0 20px;
    border-radius: 10px;
  }

  @media (max-width: 991px) {
    justify-content: flex-start;
  }
  @media (max-width: 767px) {
    display: none;
  }
`;

const MobileSizeFilterWrapper = styled.div`
  display: none;
  flex-direction: column;

  & span {
    margin-right: 10px;
    text-align: end;
    font-size: 14px;
  }

  grid-template-columns: repeat(3, 1fr);
  grid-column-gap: 10px;
  grid-row-gap: 10px;
  & button {
    border: none;
    background: #3bb77e;
    color: #fff;
    border-radius: 10px;
    font-size: 14px;
    word-break: keep-all;
    height: 30px;
    :hover {
      background-color: #fd7e14;
    }
  }

  @media (max-width: 767px) {
    display: grid;
  }
`;

const SizeInput = styled.input`
  max-width: 120px;
  height: 30px;
  text-align: center;
  padding: 0 10px;
  margin-right: 10px;
  @media (max-width: 767px) {
    font-size: 14px;
    width: 100%;
    max-width: none;
  }
`;

const SizeSelect = styled.select`
  width: fit-content;
  height: 30px;
  text-align: center;
  border: 1px solid #ececec;
  background-color: #fff;
  border-radius: 10px;
  margin-right: 10px;
  padding: 0px 20px;

  @media (max-width: 767px) {
    font-size: 14px;
    width: 100%;
    max-width: none;
  }
`;

// 사이즈 형식을 표시하는 레이블 컴포넌트 추가
const SizeFormatLabel = styled.div`
  display: flex;
  align-items: center;
  margin-right: 15px;
  font-size: 14px;
  color: #666;

  @media (max-width: 767px) {
    margin-bottom: 10px;
    font-size: 13px;
  }
`;

const ProductSizeFilter = ({
  updateProductFilters,
  products,
  productFilters,
  handleSizeFilter,
  viewHeight,
  categorySizeSearch,
  selectedCategory,
  setFilteredProducts,
  allProducts,
  setAllProducts,
  updateUnitParams,
  searchParams,
  setSearchParams,
  setPage,
  currentCategory,
  resetPagination,
}) => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const [selectedSizes, setSelectedSizes] = useState({
    horizontal: searchParams?.unit?.garo !== "0" ? searchParams.unit.garo : "",
    vertical: searchParams?.unit?.sero !== "0" ? searchParams.unit.sero : "",
    height: searchParams?.unit?.high !== "0" ? searchParams.unit.high : "",
    range: searchParams?.unit?.erange !== "0" ? searchParams.unit.erange : "",
  });

  // 페이지 진입 시 초기 값 설정
  useEffect(() => {
    updateProductFilters({
      ...productFilters,
      sizes: {
        horizontal: "",
        vertical: "",
        height: "",
        range: "",
      },
    });
    setSelectedSizes({
      horizontal: "",
      vertical: "",
      height: "",
      range: "",
    });
  }, []);

  useEffect(() => {
    setSelectedSizes({
      horizontal:
        searchParams?.unit?.garo !== "0" ? searchParams.unit.garo : "",
      vertical: searchParams?.unit?.sero !== "0" ? searchParams.unit.sero : "",
      height: searchParams?.unit?.high !== "0" ? searchParams.unit.high : "",
      range: searchParams?.unit?.erange !== "0" ? searchParams.unit.erange : "",
    });
  }, [searchParams]);

  useEffect(() => {
    if (products?.items) {
      setAllProducts(products.items);
      setFilteredProducts(products.items);
    }
  }, [products]);

  // ProductSizeFilter.js에서 handleFilter 함수 수정
  const handleFilter = () => {
    // 1. 검색 파라미터 업데이트 (페이지는 1로 초기화)
    setSearchParams((prev) => ({
      ...prev,
      unit: {
        garo: selectedSizes.horizontal || "0",
        sero: selectedSizes.vertical || "0",
        high: selectedSizes.height || "0",
        erange: selectedSizes.range || "0",
      },
      page: 1, // 검색 시 항상 1페이지로 설정
    }));

    // 2. 페이지 상태 직접 업데이트 (컴포넌트로 props 전달 필요)
    setPage(1);

    // 4. 데이터 무효화하여 새로 요청 트리거
    queryClient.invalidateQueries([
      "categoryProducts",
      currentCategory.code,
      currentCategory.hash,
      1,
    ]);

    // 5. 필터링 로직 실행
    const filtered = allProducts.filter((product) => {
      // 기존 필터링 코드...
    });

    setFilteredProducts(filtered);

    // 6. usePaginationHandlerModified의 currentPage 초기화
    if (resetPagination) {
      resetPagination(1);
    }
  };

  const onChangeSize = (id) => (e) => {
    setSelectedSizes({ ...selectedSizes, [id]: e.target.value });
  };

  const handleInput = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setTimeout(() => {
        handleFilter();
      }, 200);
    }
  };

  const sizes = [0, 10, 20, 30, 40, 50, 60];

  return (
    <>
      <SizeFilterWrapper>
        <SizeInput
          onChange={onChangeSize("horizontal")}
          placeholder={"가로mm"}
          value={selectedSizes.horizontal}
          onKeyDown={handleInput}
        />
        <SizeInput
          onChange={onChangeSize("vertical")}
          placeholder={"세로mm"}
          value={selectedSizes.vertical}
          onKeyDown={handleInput}
        />

        <SizeInput
          onChange={onChangeSize("height")}
          placeholder={"높이mm"}
          value={selectedSizes.height}
          onKeyDown={handleInput}
        />

        <SizeSelect
          id="sizeFilter"
          name="sizeFilter"
          onChange={onChangeSize("range")}
          value={selectedSizes.range}
        >
          {sizes.map((el, i) => (
            <option value={el > 0 ? el : ""} key={i}>
              ± {el}
            </option>
          ))}
        </SizeSelect>
        <button className="btn" onClick={handleFilter}>
          사이즈 검색
        </button>
      </SizeFilterWrapper>
      <MobileSizeFilterWrapper>
        <SizeInput
          onChange={onChangeSize("horizontal")}
          placeholder={"가로"}
          value={selectedSizes.horizontal}
        />
        <SizeInput
          onChange={onChangeSize("vertical")}
          placeholder={"세로"}
          value={selectedSizes.vertical}
        />

        <SizeInput
          onChange={onChangeSize("height")}
          placeholder={"높이"}
          value={selectedSizes.height}
        />

        <SizeSelect
          id="sizeFilter"
          name="sizeFilter"
          onChange={onChangeSize("range")}
          value={selectedSizes.range}
        >
          {sizes.map((el, i) => (
            <option value={el > 0 ? el : ""} key={i}>
              ± {el}
            </option>
          ))}
        </SizeSelect>
        <button onClick={handleFilter}>사이즈 검색</button>
      </MobileSizeFilterWrapper>
    </>
  );
};

const mapStateToProps = (state) => ({
  products: state.products,
  productFilters: state.productFilters,
});

const mapDidpatchToProps = {
  updateProductFilters,
};

export default connect(mapStateToProps, mapDidpatchToProps)(ProductSizeFilter);
